import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { FaPlus } from 'react-icons/fa';
import { approveBooking } from 'redux/booking/car/carBookingSlice';
import { Modal, Button } from 'components/common';
import TransactionDetailUploadAttachment from './TransactionDetailUploadAttachment';
import TransactionDetailAssignDriverForm from './TransactionDetailAssignDriverForm';

const TransactionDetailApprovalModal = ({ showModal, setshowModal, transaction }) => {
  const { register, handleSubmit } = useForm();
  const [assignDrivers, setAssignDrivers] = useState([]);
  const [files, setFiles] = useState([]);
  const dispatch = useDispatch();

  const closeModal = () => setshowModal(false);

  const validateDriverAssign = () => {
    let ret = assignDrivers.length > 0;
    assignDrivers.forEach((item) => {
      if (item.driver_id === '' || item.car_id === '') ret = false;
    });
    if (!ret) toast.error('Driver and car must be assigned.');
    return ret;
  };

  const validateDuplicateCar = () => {
    const carIdSet = new Set();
    const driverIdSet = new Set();
    let ret = true;

    assignDrivers.forEach(({ driver_id, car_id }) => {
      if (carIdSet.has(car_id)) { toast.error('Duplicate cars found.'); ret = false; } else carIdSet.add(car_id);
      if (driverIdSet.has(driver_id)) { toast.error('Duplicate drivers found.'); ret = false; } else driverIdSet.add(driver_id);
    });

    return ret;
  };

  const onSubmit = (formData) => {
    if (!validateDriverAssign()) return;
    if (!validateDuplicateCar()) return;

    const data = { driver_cars: assignDrivers, remark: formData.remark, files };

    try {
      dispatch(approveBooking({ transactionId: transaction.id, data }));
      closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddDriver = useCallback(() => {
    setAssignDrivers((prev) => [...prev, { driver_id: '', car_id: '' }]);
  }, []);

  const handleRemoveDriver = (index) => {
    setAssignDrivers((prev) => prev.filter((_, i) => i !== index));
  };

  const updateAssignDriver = (index, driver) => {
    setAssignDrivers((prev) => {
      const next = [...prev];
      next[index] = { driver_id: driver.id, car_id: driver.car_id };
      return next;
    });
  };

  return (
    <Modal
      show={showModal}
      onHide={closeModal}
      title="Approve car booking"
      size="lg"
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" type="submit" form="approve-car-booking-form" variant="success">Approve</Button>
        </>
      )}
    >
      <form id="approve-car-booking-form" onSubmit={handleSubmit(onSubmit)}>
        <h6 className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-mb-3">Assigning drivers for car booking</h6>

        {assignDrivers.map((item, index) => (
          <TransactionDetailAssignDriverForm
            index={index}
            key={index}
            driver={item}
            handleRemoveDriver={() => handleRemoveDriver(index)}
            transaction={transaction}
            updateAssignDriver={updateAssignDriver}
          />
        ))}

        <div className="tw-flex tw-justify-end tw-mb-4">
          <Button size="sm" variant="secondary" leftIcon={FaPlus} onClick={handleAddDriver}>Add Driver</Button>
        </div>

        <div className="tw-mb-4">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5">Notes</label>
          <textarea
            rows={6}
            placeholder="Notes"
            className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-solid tw-border-slate-300 tw-rounded-md tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
            {...register('remark')}
          />
        </div>

        <div>
          <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5">Attachments</label>
          <TransactionDetailUploadAttachment files={files} setFiles={setFiles} />
        </div>
      </form>
    </Modal>
  );
};

TransactionDetailApprovalModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  transaction: PropTypes.object.isRequired,
};

export default TransactionDetailApprovalModal;
